import React, { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { makePlaylistsShow } from "@api/index";
import { connectToApi, ApiConnectedOwnProps } from "@api/components/connectToApi";
import { makeNotFound } from "@app/components/notFoundHocs";
import { GlobalState } from "@app/models/state";
import { messages } from "@app/messages";
import { setTitle } from "@app/util";
import { Page, CardBody } from "@ui/Page";
import { compose } from "@util/compose";
import { messages as vMessages } from "@vendor/messages";
import { Playlist as Model, PlaylistSnapshot as SnapshotModel, Track } from "@vendor/models";
import { makeGetPlaylist, PropsWithId, getPlaylistFilters } from "@vendor/selectors";
import { Playlist } from "@vendor/components/Playlist";
import { PlaylistsFilters } from "@vendor/components/PlaylistsFilters";
import { PlaylistSnapshot } from "@vendor/components/PlaylistSnapshot";
import { TrackListTable } from "@vendor/components/TrackListTable";

interface PlaylistsViewStateProps {
    playlist: Model;
}

type PlaylistsViewProps = PlaylistsViewStateProps & ApiConnectedOwnProps &
    RouteComponentProps;

const PlaylistsView = ({ history, playlist }: PlaylistsViewProps) => {
    const intl = useIntl();
    useEffect(() => {
        setTitle(intl, messages.pagePlaylistsTitle, playlist.name);
    }, [playlist]);

    const onFilter = (id: number) => {
        if (id !== playlist.id) {
            const newPlaylist = new Model({id});
            history.push(newPlaylist.getViewUrl());
        }
    };

    const breadcrumbItems = [
        {
            text: <FormattedMessage {...messages.pagePlaylistsTitle} />,
            to: "/playlists",
        },
        {
            text: playlist.name != null
                ? playlist.name
                : <FormattedMessage {...vMessages.playlistDefaultTitle} values={{id: playlist.id}} />,
        },
    ];

    return (
        <Page className="playlists-view-page" breadcrumbs={breadcrumbItems}>
            <CardBody>
                <Playlist
                    playlist={playlist}
                    style="headline"
                    additionalDetails
                />
                <PlaylistsFilters id={playlist.id} onFilter={({ id }) => onFilter(id)} />
            </CardBody>
            <div className="playlists-view-page__snapshots">
                {playlist.snapshots != null && (playlist.snapshots as SnapshotModel[]).map((snapshot, i) => {
                    if (snapshot instanceof SnapshotModel) {
                        return (
                            <div key={i}>
                                <CardBody>
                                    <PlaylistSnapshot snapshot={snapshot} />
                                </CardBody>
                                <TrackListTable
                                    isPlaylist
                                    collapsable
                                    tracks={snapshot.tracks as Track[]}
                                />
                            </div>
                        );
                    }
                    else {
                        return <span key={i}></span>;
                    }
                })}
                {(playlist.snapshots == null || playlist.snapshots.length === 0) &&
                    <p><FormattedMessage {...vMessages.noSnapshots} /></p>
                }
            </div>
        </Page>
    );
};

// Map the upload state to the component.
const makeMapStateToProps = () => {
    const getPlaylist = makeGetPlaylist();
    return (state: GlobalState, ownProps: PropsWithId) => {
        const { id } = ownProps.params;
        const playlist = getPlaylist(state, ownProps);
        const lastLoaded = [ playlist ? playlist.lastLoaded : null ];
        // Build the action creator from the filter.
        const { from, to } = getPlaylistFilters(state);
        const actionCreators = [
            makePlaylistsShow(Number(id), from, to),
        ];
        // Force reloads when the dates change.
        // const requestId = [ id + "_" + from.format() + "_" + to.format() ];
        return {
            playlist,
            lastLoaded,
            actionCreators,
            // requestId,
        } as PlaylistsViewProps;
    };
};

const ConnectedPlaylistsView = compose<PlaylistsViewProps, {}>(
    withRouter,
    connect(makeMapStateToProps, null),
    connectToApi({
        showRefresh: false,
        cardPage: true,
    }),
    makeNotFound("playlist"),
)(PlaylistsView);

export {
    PlaylistsView as PlaylistsViewPresenter,
    ConnectedPlaylistsView as PlaylistsView,
};
